import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, KeyVal, Page } from 'equasion-lib';
import ZonePickerModal, { useZoneScanOnLoad } from '@/lib/shared-coms/ZonePickerModal';
import ActionTopBar from '@/lib/shared-coms/ActionTopBar';
import { FiAlertTriangle, FiCheckCircle, FiEdit } from 'react-icons/fi';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import { scannedLotVariables } from '@/lib/scanning/scannedLot';
import { scanAlert } from '@/lib/scanning/scanAlert';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import { stockOverageNotice } from './stockOverage';
import './Stock.scss';

function Stock({
  serializedItemState,
  bulkInventoryEventCreate,
  selectedCompanyId,
  scannedLot = null
}) {
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const [stocked, setStocked] = useState(false);
  const {
    purchaseOrder,
    pedigree,
    serialNumber,
    gtin,
    serializedItemType,
    itemBreakdown,
    product: scannedProduct
  } = serializedItemState || {};
  const { id: purchaseOrderId, clientPurchaseOrderNumber } = purchaseOrder || {};
  const { id: defaultZoneId, name: defaultZoneName = '' } = purchaseOrder?.defaultZone || {};
  const [assignedZoneId, setAssignedZoneId] = useState(defaultZoneId);
  const [assignedZoneName, setAssignedZoneName] = useState(defaultZoneName);
  const { ITEM } = pedigree || {};
  const isZoneAssigned = Boolean(assignedZoneId);
  // The zone is the only thing this screen asks for, so its scanner opens with the screen —
  // unless the purchase order already brought a default zone.
  const { tapPrompt, pickerProps } = useZoneScanOnLoad(
    () => setModalOpen(true),
    !isZoneAssigned
  );
  const productTitle = (itemBreakdown || []).find(({ product }) => product?.title)
    ?.product?.title;
  // A unit names its own product; a case names the products inside it.
  const overageNotice = stockOverageNotice(purchaseOrder?.stockingProgress, [
    scannedProduct?.id,
    ...(itemBreakdown || []).map(({ product }) => product?.id)
  ]);

  const submitBulkProductUpdate = async () => {
    try {
      setLoading(true);
      await bulkInventoryEventCreate({
        variables: {
          serialNumber,
          gtin,
          // The scanned barcode's lot / expiration — recorded only on a unit that has none on file.
          ...scannedLotVariables(scannedLot || {}),
          companyId: selectedCompanyId,
          inventoryEventAttributes: {
            zoneId: assignedZoneId,
            purchaseOrderId,
            type: 'STOCK',
            companyId: selectedCompanyId
          }
        }
      });
      setStocked(true);
    } catch (e) {
      console.log(`ERROR UPDATING PRODUCTS: ${e}`);
      // A refusal (e.g. it would stock more than the PO ordered) must reach the stocker.
      scanAlert('Not stocked', operatorFacingMessage(e, 'The unit could not be stocked. Try again.'), 10);
    }
    setLoading(false);
  };

  return (
    <Page className="stock action-page">
      <ActionTopBar
        action="Stock"
        serialNumber={serialNumber}
        actionColor="var(--b600)"
      />
      <div className="action-page-body">
        <KeyVal
          items={[
            {
              key: 'PO Number',
              value: clientPurchaseOrderNumber
            },
            {
              key: 'Serial',
              value: serialNumber
            }
          ].filter(Boolean)}
        />
        {overageNotice && (
          <div className="stock-overage">
            <div className="stock-overage__callout">
              <FiAlertTriangle aria-hidden />
              <div>
                <strong>EPCIS lists more units than this PO ordered</strong>
                <span>
                  Stock only what is physically here. Serials that are not stocked are marked
                  not received when the PO closes.
                </span>
              </div>
            </div>
            {overageNotice.lines.map((line) => (
              <div className="stock-overage__line" key={line.productId}>
                {line.title && <strong>{line.title}</strong>}
                <span>{line.message}</span>
                <b className="stock-overage__count">
                  Stocked {line.stockedQuantity} of {line.orderedQuantity}
                </b>
              </div>
            ))}
          </div>
        )}
        <div className="assigned-zone" onClick={() => setModalOpen(true)}>
          <FiEdit />
          <h1>Assigned Zone</h1>
          <h2 className={!assignedZoneName ? 'zone-placeholder' : ''}>
            {assignedZoneName || tapPrompt}
          </h2>
        </div>
        <div className="header-row">
          <div className="row-header">ITEM</div>
          <div className="row-header">QTY</div>
          <div className="row-header">STATUS</div>
        </div>
        {(itemBreakdown || []).map(({ status, quantity, product, lot, expiration }) => (
          <Fragment key={lot}>
            <div className="data-row">
              <div className="row-data">
                {product ? (
                  <WarehouseProductDisplay product={product} />
                ) : (
                  'SSCC'
                )}
                <br />
                {lot} / {formatDateOnly(expiration) || expiration}
              </div>
              <div className="row-data">{quantity}</div>
              <div className="row-data">{status}</div>
            </div>
          </Fragment>
        ))}
        <Button
          type="primary"
          fullWidth
          onClick={submitBulkProductUpdate}
          disabled={loading || !isZoneAssigned}
        >
          Mark as Stocked
        </Button>
      </div>
      <div className={['result-panel', stocked && 'open'].filter(Boolean).join(' ')}>
        <div className="result-message">
          <FiCheckCircle style={{ color: 'var(--green)' }} />
          <h1>Stocked Successfully</h1>
          <p>
            {ITEM} products from PO # {clientPurchaseOrderNumber} have been stocked in
            zone
          </p>
        </div>
        <div className="result-actions">
          <Button type="primary" fullWidth onClick={() => navigate('/')}>
            Done
          </Button>
        </div>
      </div>
      <ZonePickerModal
        className="stock-zone-modal"
        open={modalOpen}
        {...pickerProps(() => setModalOpen(false))}
        title="Select a zone"
        companyId={selectedCompanyId}
        zoneTypes={['stock']}
        scanTitle="Scan stock zone"
        scanHint={`Serial ${serialNumber}${productTitle ? ` · ${productTitle}` : ''}${
          clientPurchaseOrderNumber ? ` (PO # ${clientPurchaseOrderNumber})` : ''
        }. Scan the label on the zone it is going into.`}
        onZoneChosen={({ id, display }) => {
          setAssignedZoneId(id);
          setAssignedZoneName(display);
        }}
      />
    </Page>
  );
}

export default Stock;
